import React, { ComponentProps, useState } from 'react';
import {
  CheckBoxParam,
  ComboBoxParam,
  Form,
  Input,
  Radio,
  RadioGroup,
  RadioParam,
  Row,
  SonForm,
  SubmitButton,
  Switch,
  TextParam,
  useForm,
} from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import FormsRow, { FormsRowModifier } from '../../../components/molecules/Form/components/FormsRow/FormsRow';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { ContentSelectComboBoxForForm2, ListBoxModifier } from '../../../components/molecules/Form/components/ComboBox';

export type PdfFileDetailsForm = SonForm<
  | TextParam<'title'>
  | ComboBoxParam<'fileResourceReference'>
  | TextParam<'caption'>
  | RadioParam<'orientation', 'portrait' | 'landscape'>
  | CheckBoxParam<'popUp'>
  | RadioParam<'size', 'small' | 'medium' | 'large'>
>;

type ExternalContentEditProps = {
  form: PdfFileDetailsForm;
};

function PdfFileDetailsEdit({ form }: ExternalContentEditProps) {
  const { t } = useSonTranslation('PdfFileDetails');

  const { delayedRefresh } = useRefreshBlock();

  const { formProps, params, fetchState } = useForm({
    form,
    onSuccess: delayedRefresh,
  });

  const titleParam = params.findByName('title');
  const fileResourceReferenceParam = params.findByName('fileResourceReference');
  const captionParam = params.findByName('caption');
  const sizeCombinationParam = params.getAllByName('size');
  const orientationCombinationParam = params.getAllByName('orientation');
  const popUpParam = params.findByName('popUp');

  const [popUpEnabled, setPopUpEnabled] = useState(popUpParam ? popUpParam?.checked : false);

  return (
    <Form id="external-content-form" {...formProps}>
      {titleParam && (
        <Row>
          <Input label={t('input.title.label')} helpText={t('input.title.helpText')} param={titleParam} />
        </Row>
      )}
      {fileResourceReferenceParam && (
        <Row modifier="block">
          <ContentSelectComboBoxForForm2
            name={fileResourceReferenceParam.name}
            suggestUrl={fileResourceReferenceParam.comboBoxUrl.url}
            required={fileResourceReferenceParam.required}
            selected={fileResourceReferenceParam.selected}
            labelTitle={t('input.fileResourceReference.label')}
            labelHelpText={t('input.fileResourceReference.helpText')}
            modifierListBox={ListBoxModifier.RELATIVE}
            valueKey="resourceReference"
          />
        </Row>
      )}
      {captionParam && (
        <Row>
          <Input label={t('input.caption.label')} param={captionParam} />
        </Row>
      )}
      {sizeCombinationParam.length > 1 && (
        <RadioGroup
          fieldsetModifier={['like-label', 'inline-radios']}
          legend={t('input.size.fieldSetLabel')}
          required={orientationCombinationParam.some(r => r.required)}
        >
          {sizeCombinationParam.reverse().map(radio => (
            <Radio key={radio.value} label={t(`input.size.label.${radio.value}`)} param={radio} />
          ))}
        </RadioGroup>
      )}
      {orientationCombinationParam.length > 1 && (
        <RadioGroup
          fieldsetModifier={['like-label', 'inline-radios']}
          legend={t('input.orientation.fieldSetLabel')}
          required={orientationCombinationParam.some(r => r.required)}
        >
          {orientationCombinationParam.map(radio => (
            <Radio key={radio.value} label={t(`input.orientation.label.${radio.value}`)} param={radio} />
          ))}
        </RadioGroup>
      )}
      {popUpParam && (
        <FormsRow modifier={FormsRowModifier.RIGHT}>
          <Switch param={popUpParam} label={t('input.popUp.label')} onClick={() => setPopUpEnabled(!popUpEnabled)} />
        </FormsRow>
      )}
      <SubmitButton
        buttonLabel={t('submit')}
        messageSuccess={t('status.success')}
        messageSending={t('status.sending')}
        messageFail={t('status.fail')}
        fetchState={fetchState}
      />
    </Form>
  );
}

export const PdfFileDetailsEditView = asFetchableView(PdfFileDetailsEdit);
export type PdfFileDetailsEditView = ComponentProps<typeof PdfFileDetailsEditView>;
